// apps/roleforge-app/src/components/audit/ActivationMomentDeepDiveCard.tsx
//
// Plan 3e-ii Task 5.5 — Per-opportunity deep-dive renderer for the
// Activation Moment screen. Replaces (functionally) the legacy
// `OppDeepDiveCard` from Plan 3b-ii's `PerOpportunityDeepDive.tsx`.
//
// Key constraint (§13): zero agency CTAs. Hard-effort items here have
// only a silent effort label — no "Talk to our agency" link.

import type { Opportunity } from '../../lib/api-client';
import { getEngineColor } from '@vibecontrol/shared/roleforge';

interface Props {
  opp: Opportunity;
}

export function ActivationMomentDeepDiveCard({ opp }: Props) {
  const color = getEngineColor(opp.engine);
  const m = opp.metadata ?? {};
  const currentState =
    typeof m.current_state === 'string'
      ? m.current_state
      : (opp.description ?? 'Opportunity identified during discovery.');
  const futureState =
    typeof m.future_state === 'string' ? m.future_state : deriveFutureState(opp);
  const anticipatedImpact =
    typeof m.anticipated_impact === 'string' ? m.anticipated_impact : deriveAnticipatedImpact(opp);
  const isGap = opp.candidate_class === 'gap';
  const isFoundation = opp.candidate_class === 'foundation';

  return (
    <div
      className={
        'rounded-lg bg-card overflow-hidden border ' +
        (isGap
          ? 'border-coral border-l-4'
          : isFoundation
            ? 'border-dashed border-line'
            : 'border-line')
      }
      style={
        !isGap && !isFoundation ? { borderLeftColor: color, borderLeftWidth: 4 } : undefined
      }
    >
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold text-ink flex-1">
            {isGap && (
              <span className="mr-1.5 inline-flex items-center rounded-full bg-brand-coral/10 px-2 py-0.5 text-xs font-medium text-coral">
                multi-source
              </span>
            )}
            {isFoundation && (
              <span className="mr-1.5 inline-flex items-center rounded-full bg-surface px-2 py-0.5 text-xs font-medium text-graphite">
                [foundation]
              </span>
            )}
            {opp.name}
          </h3>
          <span
            className="shrink-0 inline-flex items-center rounded-full bg-surface px-2.5 py-0.5 text-xs font-medium text-graphite"
            data-testid="effort-label"
          >
            effort: {opp.effort}
          </span>
        </div>

        <div className="flex flex-col gap-3">
          <Subsection title="Current State" value={currentState} testId="current-state" />
          <Subsection title="Future State" value={futureState} testId="future-state" />
          <Subsection
            title="Anticipated Impact"
            value={anticipatedImpact}
            testId="anticipated-impact"
          />
          {isGap && <GapVerbatimQuotes opp={opp} />}
        </div>
      </div>
    </div>
  );
}

function deriveFutureState(opp: Opportunity): string {
  if (opp.asset_type) {
    return `${labelize(opp.asset_type)} supports this workflow so it becomes repeatable instead of owner-dependent.`;
  }

  if (opp.funnel_type) {
    return `A ${labelize(opp.funnel_type)} workflow moves this from ad hoc follow-up into a repeatable process.`;
  }

  return 'This becomes a repeatable AI-assisted workflow instead of an owner-dependent task.';
}

function deriveAnticipatedImpact(opp: Opportunity): string {
  const roi = opp.metadata?.roi;
  const annualSavings =
    roi !== null &&
    typeof roi === 'object' &&
    typeof (roi as Record<string, unknown>).annual_savings === 'number'
      ? (roi as Record<string, number>).annual_savings
      : null;

  if (annualSavings !== null) {
    return `Estimated annual value: ${formatCurrency(annualSavings)}.`;
  }

  return `High-priority opportunity with impact score ${opp.business_impact}/10 and ease score ${opp.ease_score}/10.`;
}

function labelize(value: string): string {
  return value.replace(/[_-]+/g, ' ');
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function Subsection({
  title,
  value,
  testId,
}: {
  title: string;
  value: string;
  testId: string;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-graphite uppercase tracking-wide mb-0.5">
        {title}
      </p>
      <p className="text-sm text-ink" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

function GapVerbatimQuotes({ opp }: { opp: Opportunity }) {
  const raw = opp.metadata?.gap_quotes;
  const quotes: { respondent: string; text: string }[] = Array.isArray(raw)
    ? raw.filter((q): q is { respondent: string; text: string } =>
        q !== null &&
        typeof q === 'object' &&
        typeof (q as Record<string, unknown>).respondent === 'string' &&
        typeof (q as Record<string, unknown>).text === 'string',
      )
    : [];
  if (quotes.length === 0) return null;
  return (
    <div className="mt-2 rounded bg-brand-coral/5 border border-coral/40 p-3 flex flex-col gap-1.5">
      {quotes.map((q, i) => (
        <p key={i} className="text-xs text-ink">
          <span className="font-semibold">{q.respondent} says:</span>{' '}
          <em>"{q.text}"</em>
        </p>
      ))}
    </div>
  );
}
